import { useEffect, useRef, useState } from 'react';

/**
 * Executes a callback at a given interval
 * @param callback - The function to execute at each interval
 * @param interval - The time in milliseconds to wait between executions
 * @param shouldStop - A function that returns a boolean indicating whether the interval should stop
 * @param limit - Optional maximum number of times the callback should be executed
 */
const usePoll = <T> (
  callback: () => Promise<T>,
  interval: number,
  shouldStop: (result: T | undefined) => boolean,
  limit?: number,
): {result: T | undefined; isPolling: boolean } => {
  const savedCallback = useRef(callback);
  const executionCount = useRef(0);
  const [ result, setResult ] = useState<T>();
  const [ isPolling, setIsPolling ] = useState(true);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isPolling) return;

    const tick = async () => {
      const currentResult = await savedCallback.current();
      setResult(currentResult);

      if (shouldStop(currentResult)) {
        setIsPolling(false);

        return;
      }

      executionCount.current += 1;

      if (limit !== undefined && executionCount.current >= limit) {
        setIsPolling(false);
      }
    };

    const intervalId = setInterval(tick, interval);

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ interval, shouldStop, limit, isPolling ]);

  return { result, isPolling };
};

export default usePoll;
